import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { useNavigate } from 'react-router-dom';
import { resetChatDrillIn } from 'src/components/ai/chat/chat-hooks/useSelectedPlaybookRun';
import { useDispatchNotification } from 'src/helpers/snackbars';

interface UseThreadDeepLinkHandlersArgs {
  workflowId: UUID;
  /** When present, deep links use the task-scoped thread route instead of the workflow-scoped one. */
  taskId?: UUID;
}

interface ThreadDeepLinkHandlers {
  /** Copies a shareable deep link to the thread; toasts on success and on clipboard failure. */
  onCopyThreadLink: (threadId: UUID) => void;
  /** Opens the thread's full chat view (drawer). */
  onViewThread: (threadId: UUID) => void;
}

/**
 * Shared copy-link / view-thread wiring for the activity surfaces. The task panel deep-links to
 * the task-scoped thread route and the workflow panel to the workflow-scoped one — that is the
 * only difference between the two consumers, so the route builder is picked by `taskId` presence.
 */
export function useThreadDeepLinkHandlers({
  taskId,
  workflowId,
}: UseThreadDeepLinkHandlersArgs): ThreadDeepLinkHandlers {
  const navigate = useNavigate();
  const dispatchLinkCopied = useDispatchNotification({
    message: 'Thread link copied',
    variant: 'success',
  });
  const dispatchCopyFailed = useDispatchNotification({
    message: "Couldn't copy thread link",
    variant: 'error',
  });

  const buildThreadPath = useCallback(
    (threadId: UUID) =>
      taskId
        ? link.WorkflowDetailTabTaskThread({ taskId, threadId, workflowId })
        : link.WorkflowDetailTabThread({ threadId, workflowId }),
    [taskId, workflowId]
  );

  const onViewThread = useCallback(
    (threadId: UUID) => {
      /**
       * Viewing a thread from an activity card should present it fresh. The
       * singleton chat may already have this thread open elsewhere with a
       * playbook run drilled in; an explicit click must not inherit that.
       */
      resetChatDrillIn();
      navigate(buildThreadPath(threadId));
    },
    [buildThreadPath, navigate]
  );

  const onCopyThreadLink = useCallback(
    (threadId: UUID) => {
      /**
       * Toast only once the clipboard write resolves — `writeText` can reject (no focus, no
       * permission), and confirming "copied" before it settles would lie to the user. The catch
       * also keeps a rejected write from becoming an unhandled promise rejection.
       */
      navigator.clipboard
        .writeText(`${window.location.origin}${buildThreadPath(threadId)}`)
        .then(() => dispatchLinkCopied())
        .catch(() => dispatchCopyFailed());
    },
    [buildThreadPath, dispatchCopyFailed, dispatchLinkCopied]
  );

  return { onCopyThreadLink, onViewThread };
}
